/**
 * Hook for managing multiple kanban boards
 * @remarks
 * Handles board creation, selection, and switching using Apollo-generated hooks
 * for all GraphQL operations. Board visibility filtering is handled by the backend
 * via the listMyKanbanBoards query - this hook only filters by sourceType.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import type { Reference } from "@apollo/client";

import { activeBoardIdVar } from "../stores/kanbanReactiveVars";

import { useAllMyKanbanBoards } from "./useAllMyKanbanBoards";

import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import {
  KanbanBoard,
  KanbanPipelineTemplate,
  KanbanSourceType,
  useCreateKanbanBoardMutation,
  useDeleteKanbanBoardMutation,
  useUpdateKanbanBoardMutation,
  useWatchlistListMyUserWatchlistsQuery,
} from "@/generated/graphql";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";
import { generateUUID } from "@/utils/uuid";

/**
 * Interface representing the state of the board manager
 */
interface BoardManagerState {
  boards: KanbanBoard[];
  activeBoard: KanbanBoard | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for managing multiple kanban boards
 * @param sourceType - Filter boards by source type (WATCHLIST or ROSTER)
 * @returns Object with boards array, activeBoard, loading/error state, and CRUD operations for board management
 */
export function useBoardManager(sourceType: KanbanSourceType) {
  // Get current user for board ownership
  const currentUser = useCurrentUser();

  // Get user's watchlists to use as sourceId for new boards
  const {
    data: watchlistData,
    loading: watchlistLoading,
    refetch: refetchWatchlists,
  } = useWatchlistListMyUserWatchlistsQuery({
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    fetchPolicy: "cache-first",
  });

  // Query for user's boards.
  //
  // SE-6575: this used to be a bare `{ first: 40 }` with no `after` and no
  // `fetchMore`, so an org past 40 boards silently lost every board beyond the
  // first page. `useAllMyKanbanBoards` walks the whole connection instead and
  // reports `hasUnreachableBoards` when it cannot (page cap / failed page), so
  // the UI can say the list is incomplete rather than truncating in silence.
  const {
    edges: boardEdges,
    loading,
    isWalkingPages,
    error: queryError,
    refetch,
    hasUnreachableBoards,
  } = useAllMyKanbanBoards();

  // Mutations
  //
  // Cache strategy: all mutations avoid refetching `ListMyKanbanBoards`.
  // Refetching after a mutation against a writer-replica split (Aurora reader
  // + lag) routinely returns the *pre-mutation* board list, clobbering the
  // entity cache with stale data and stranding the UI on the old name (or
  // stranding a just-deleted board in the tab strip). Instead:
  //   • createBoardMutation: adds the new edge to the connection via
  //     cache.modify so the new board appears immediately in the strip.
  //   • updateBoardMutation: relies on Apollo's normalized cache, which
  //     writes the mutation response directly to `KanbanBoard:<id>`.
  //   • deleteBoardMutation: prunes the edge from the connection via
  //     cache.modify, then evicts the entity and garbage-collects.
  // Background queries with `cache-and-network` will eventually reconcile
  // once the read replica catches up; we don't need to force the round-trip
  // on the hot path.
  const [createBoardMutation, { loading: isCreating, error: createError }] =
    useCreateKanbanBoardMutation({
      update: (cache, { data }) => {
        const newBoard = data?.createKanbanBoard;
        if (!newBoard) return;
        const newBoardRef = cache.identify({
          __typename: "KanbanBoard",
          id: newBoard.id,
        });
        if (!newBoardRef) return;
        cache.modify({
          fields: {
            listMyKanbanBoards(existingConnection = {}) {
              const existingEdges: unknown[] = existingConnection.edges ?? [];
              const newEdge = {
                __typename: "KanbanBoardEdge",
                node: { __ref: newBoardRef },
                cursor: newBoard.id,
              };
              return {
                ...existingConnection,
                edges: [...existingEdges, newEdge],
              };
            },
          },
        });
      },
    });

  const [updateBoardMutation, { loading: isUpdating, error: updateError }] =
    useUpdateKanbanBoardMutation();

  const [deleteBoardMutation, { loading: isDeleting, error: deleteError }] =
    useDeleteKanbanBoardMutation({
      update: (cache, { data }) => {
        const deletedId = data?.deleteKanbanBoard?.id;
        if (!deletedId) return;
        cache.modify({
          fields: {
            listMyKanbanBoards(existingConnection = {}, { readField }) {
              const existingEdges: Reference[] = existingConnection.edges ?? [];
              return {
                ...existingConnection,
                edges: existingEdges.filter((edge: Reference) => {
                  const node = readField<Reference>("node", edge);
                  return readField<string>("id", node) !== deletedId;
                }),
              };
            },
          },
        });
        cache.evict({
          id: cache.identify({ __typename: "KanbanBoard", id: deletedId }),
        });
        cache.gc();
      },
    });

  // Get first watchlist ID for board creation
  const firstWatchlistId = useMemo(() => {
    const firstWatchlist =
      watchlistData?.listMyUserWatchlists?.edges?.[0]?.node?.watchlist;
    return firstWatchlist?.id || null;
  }, [watchlistData?.listMyUserWatchlists?.edges]);

  /**
   * Resolves the watchlist a new WATCHLIST board binds as its `sourceId`,
   * re-asking the server before concluding there is none.
   *
   * The watchlist query above is `cache-first` and fires once, at mount. When
   * that single attempt fails — an aborted or timed-out request batch under a
   * loaded backend is the usual way — `firstWatchlistId` stays null for the
   * whole session, and every "New" press is refused with "Please create a
   * watchlist first", which is false for a user who has watchlists. Nothing
   * refires the query, so the surface stays broken until a reload.
   *
   * Re-asking on demand costs one request on the path that was about to fail
   * anyway and leaves the refusal meaning exactly what it says.
   * @returns The watchlist id, or null when the user genuinely has none
   */
  const resolveFirstWatchlistId = useCallback(async (): Promise<
    string | null
  > => {
    if (firstWatchlistId) return firstWatchlistId;
    try {
      const { data } = await refetchWatchlists();
      return (
        data?.listMyUserWatchlists?.edges?.[0]?.node?.watchlist?.id ?? null
      );
    } catch {
      return null;
    }
  }, [firstWatchlistId, refetchWatchlists]);

  // Parse boards from GraphQL data. `boardEdges` is already de-duplicated by
  // board id, so the create path's cache.modify edge and the same board
  // arriving in a later page collapse to one entry.
  const allBoards = useMemo(
    () => boardEdges.map(edge => edge.node as KanbanBoard),
    [boardEdges]
  );

  // Filter boards by sourceType (backend handles user access filtering)
  const filteredBoards = useMemo(() => {
    return allBoards.filter(board => board.sourceType === sourceType);
  }, [allBoards, sourceType]);

  // Resolve initial active board synchronously from reactive var + cache data
  const initialActiveBoard = useMemo((): KanbanBoard | null => {
    const persistedId = activeBoardIdVar();
    const match = persistedId
      ? filteredBoards.find(b => b.id === persistedId)
      : undefined;

    // SE-6575: mounting against a PARTIAL cache (page 1 of a multi-page board
    // connection) must not resolve to an arbitrary board. Doing so would set a
    // non-null `activeBoard` of the right sourceType, which makes the restore
    // effect below permanently inert — so the user's persisted board could
    // never be adopted once the walk delivered it. Start null instead and let
    // the effect restore properly.
    if (persistedId && !match && isWalkingPages) return null;

    return match ?? filteredBoards[0] ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- One-time initialization from cache
  }, []);

  // State
  const [state, setState] = useState<BoardManagerState>({
    boards: [],
    activeBoard: initialActiveBoard,
    isLoading: true,
    error: null,
  });

  const sourceTypeFromBoard = state.activeBoard?.sourceType;
  const hasBoardsOfType = filteredBoards.length > 0;

  // Update board name
  const updateBoardName = useCallback(
    async (boardId: string, name: string) => {
      const { data: mutationData } = await updateBoardMutation({
        variables: {
          id: boardId,
          input: {
            name,
          },
        },
      });

      if (!mutationData?.updateKanbanBoard) {
        throw new Error("Failed to update board");
      }

      return mutationData.updateKanbanBoard as KanbanBoard;
    },
    [updateBoardMutation]
  );

  // Create a new board
  //
  // SE-5487: PIPELINE boards are created from a recruitment template. The FE
  // sends only the `pipelineTemplate` enum and the BE seeds the stages (CUSTOM
  // seeds zero) — no phases and no sourceId are sent for a pipeline. WATCHLIST
  // and ROSTER creation are unchanged: WATCHLIST binds the watchlist id as
  // sourceId, ROSTER gets a unique generated sourceId.
  const createBoard = useCallback(
    async (name: string, pipelineTemplate?: KanbanPipelineTemplate) => {
      // Only check for watchlist if creating a watchlist board
      const watchlistId =
        sourceType === KanbanSourceType.Watchlist
          ? await resolveFirstWatchlistId()
          : null;
      if (sourceType === KanbanSourceType.Watchlist && !watchlistId) {
        throw new Error(
          "Please create a watchlist first before creating a Watchlist board"
        );
      }

      if (!currentUser?.id) {
        throw new Error("User not authenticated");
      }

      // WATCHLIST → the watchlist id; ROSTER → a unique UUID per board instance;
      // PIPELINE → no sourceId (the BE owns the board's stages via the template).
      const sourceId =
        sourceType === KanbanSourceType.Watchlist
          ? watchlistId || undefined
          : sourceType === KanbanSourceType.Roster
            ? generateUUID()
            : undefined;

      const { data: mutationData } = await createBoardMutation({
        variables: {
          input: {
            name,
            sourceType,
            ...(sourceId && { sourceId }), // Only include sourceId if defined
            ...(pipelineTemplate && { pipelineTemplate }),
          },
        },
      });

      if (!mutationData?.createKanbanBoard) {
        throw new Error("Failed to create board");
      }

      const newBoard = mutationData.createKanbanBoard as KanbanBoard;

      // SE-5508 `pipeline_created`. Emitted HERE — the one mutation site —
      // rather than in `usePipelineCreationFlow.handleCreate`, so the two
      // wrappers above it (`useCreatePipeline`, then the modal's submit
      // handler) cannot each contribute an emission. It sits after the
      // `!mutationData?.createKanbanBoard` throw above, so a create that
      // failed or resolved empty emits nothing.
      //
      // Only PIPELINE boards are a "pipeline"; WATCHLIST/ROSTER creation is a
      // different product object and is deliberately not instrumented here.
      //
      // `from_migration` is unreachable from this path by construction: a
      // migrated board is one the SE-5505 backfill produced with a null
      // template, and every client create sends one. See
      // `TPipelineCreationSource`.
      if (sourceType === KanbanSourceType.Pipeline) {
        void AnalyticsTracker.pipeline.logPipelineCreated({
          source:
            pipelineTemplate === KanbanPipelineTemplate.Custom
              ? "custom"
              : "template",
          template: pipelineTemplate ?? "NONE",
          board_id: newBoard.id,
        });
      }

      // Set the newly created board as active immediately
      setState(prev => ({
        ...prev,
        activeBoard: newBoard,
      }));
      activeBoardIdVar(newBoard.id);

      return newBoard;
    },
    [createBoardMutation, resolveFirstWatchlistId, sourceType, currentUser?.id]
  );

  // Delete a board
  const deleteBoard = useCallback(
    async (boardId: string) => {
      await deleteBoardMutation({
        variables: {
          id: boardId,
        },
      });

      // After deletion, if the deleted board was active, switch to first remaining board
      if (state.activeBoard?.id === boardId) {
        const remainingBoards = filteredBoards.filter(b => b.id !== boardId);
        const nextBoard = remainingBoards[0] || null;
        setState(prev => ({
          ...prev,
          activeBoard: nextBoard,
        }));
        activeBoardIdVar(nextBoard?.id ?? null);
      }
    },
    [deleteBoardMutation, state.activeBoard?.id, filteredBoards]
  );

  // Switch to a different board.
  //
  // SE-7120: the return value is the miss signal. This used to be `void`, so a
  // caller could not tell "switched" from "silently did nothing" — a target
  // outside `filteredBoards` (wrong source type, or a board the page walk has
  // not reached yet) left the previous board active and reported nothing, and
  // every caller carried on as though it had worked. Reporting the outcome is
  // what lets `useBoardDeepLink` retry a still-arriving board and surface a
  // genuinely unresolvable one, instead of stranding the user on an unrelated
  // board with no explanation.
  const switchBoard = useCallback(
    (boardId: string): boolean => {
      const board = filteredBoards.find(b => b.id === boardId);
      if (!board) {
        return false;
      }
      setState(prev => ({ ...prev, activeBoard: board }));
      activeBoardIdVar(boardId);
      return true;
    },
    [filteredBoards]
  );

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Update state when data changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- ANTI-PATTERN: Copying Apollo cache to local state. FIX: Use Apollo data directly, derive loading/error with useMemo
    setState(prev => ({
      ...prev,
      boards: allBoards,
      isLoading:
        loading || watchlistLoading || isCreating || isUpdating || isDeleting,
      error:
        queryError?.message ||
        createError?.message ||
        updateError?.message ||
        deleteError?.message ||
        null,
    }));
  }, [
    allBoards,
    loading,
    watchlistLoading,
    isCreating,
    isUpdating,
    isDeleting,
    queryError,
    createError,
    updateError,
    deleteError,
  ]);

  // Set initial active board when sourceType changes or on remount
  useEffect(() => {
    // Skip while data is still loading to avoid clearing persisted board ID
    if (loading) return;

    // When switching sourceType, update activeBoard to first board of that type
    if (hasBoardsOfType && sourceTypeFromBoard !== sourceType) {
      // Restore previously active board from reactive var if it exists in this sourceType
      const persistedId = activeBoardIdVar();
      const restoredBoard = persistedId
        ? filteredBoards.find(b => b.id === persistedId)
        : undefined;

      // SE-6575: `loading` goes false as soon as page 1 lands, but the board
      // list is not whole until the page walk finishes. Falling back to
      // `filteredBoards[0]` here would overwrite the user's persisted pipeline
      // with an arbitrary one purely because their board had not arrived yet —
      // and the overwrite is permanent, since this effect never fires again
      // once `sourceTypeFromBoard === sourceType`. Wait for the walk instead.
      // Only the fallback waits: a persisted board already present in the
      // partial list is restored immediately, and a session with no persisted
      // board still selects one on the first page.
      if (persistedId && !restoredBoard && isWalkingPages) return;

      const targetBoard = restoredBoard ?? filteredBoards[0];

      if (targetBoard) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- ANTI-PATTERN: Adjusting state on sourceType change. FIX: Use key={sourceType} on hook consumer to reset
        setState(prev => ({
          ...prev,
          activeBoard: targetBoard,
        }));
        activeBoardIdVar(targetBoard.id);
      }
    } else if (
      !hasBoardsOfType &&
      sourceTypeFromBoard !== sourceType &&
      // SE-6575: the connection is newest-first across ALL source types, so
      // page 1 can legitimately hold zero boards of THIS sourceType while later
      // pages hold several. Clearing the persisted board id here on that
      // evidence would wipe the user's pipeline over a list that is merely
      // incomplete. Once the walk ends, an empty result is real.
      !isWalkingPages
    ) {
      // No boards of this type - explicitly set activeBoard to null
      // This allows UI to render empty state with create board button

      setState(prev => ({
        ...prev,
        activeBoard: null,
      }));
      activeBoardIdVar(null);
    }
  }, [
    loading,
    isWalkingPages,
    filteredBoards,
    hasBoardsOfType,
    sourceType,
    sourceTypeFromBoard,
  ]);

  // Sync activeBoard with latest data from filteredBoards (SE-3489)
  // This ensures activeBoard reflects updates (e.g., name changes) after refetch
  useEffect(() => {
    if (!state.activeBoard) {
      return;
    }

    const updatedBoard = filteredBoards.find(
      b => b.id === state.activeBoard?.id
    );

    // Only update if the board data has changed (e.g., name updated)
    if (updatedBoard && updatedBoard.name !== state.activeBoard.name) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Syncing external GraphQL data with local state (SE-3489)
      setState(prev => ({
        ...prev,
        activeBoard: updatedBoard,
      }));
    }
  }, [filteredBoards, state.activeBoard]);

  return {
    // State
    boards: filteredBoards,
    activeBoard: state.activeBoard,
    isLoading: state.isLoading,
    error: state.error,
    /**
     * SE-6575: true when boards exist that the walk could not reach (page cap
     * or a failed page). Consumers must indicate more boards exist rather than
     * rendering a silently-truncated list.
     */
    hasUnreachableBoards,
    /**
     * SE-7120: true while the connection walk is still fetching pages, so the
     * board set can still grow. A `boardId` absent from `boards` right now is
     * only PROVISIONALLY absent while this holds — consumers must not treat
     * such a miss as final (see `useBoardDeepLink`).
     */
    isResolvingBoardSet: isWalkingPages,

    // Actions
    switchBoard,
    createBoard,
    updateBoardName,
    deleteBoard,
    clearError,
    refresh: refetch,
  };
}
